/**
 * ч.1381 — «Платник ПДВ» в картці й майстрі організації: обовʼязковий вибір «так / ні».
 *
 * Рішення власника: бухгалтер, що створює організацію, може забути про ПДВ, і галочка
 * лишала неплатника (ТОВ «ГРІН ЗЕД ЕНЕРДЖІ» — у 1С платник, у картці ні). Тепер у нової
 * організації статус не заданий, поки його не оберуть; «так» вимагає ІПН платника ПДВ.
 * ФОП 1, 2 і 3 групи 5 % — неплатники, 3 група 3 % — платник: статус задає система.
 */
import type { OrganizationCreate } from '@/api/catalogs';

/** Статус, який задає система оподаткування (null — бухгалтер обирає сам). */
export function vatFixedFor(taxSystem: OrganizationCreate['tax_system']): boolean | null {
  if (taxSystem === 'FOP_1' || taxSystem === 'FOP_2' || taxSystem === 'FOP_3_5') return false;
  if (taxSystem === 'FOP_3_3') return true;
  return null;
}

/** Статус ПДВ після зміни системи оподаткування: зі «своєї» групи на систему з вибором —
 *  знову «оберіть» (попереднє «ні» задала група, а не бухгалтер). */
export function vatAfterTaxSystem(
  from: OrganizationCreate['tax_system'], to: OrganizationCreate['tax_system'],
  current: boolean | null,
): boolean | null {
  const fixed = vatFixedFor(to);
  if (fixed !== null) return fixed;
  return vatFixedFor(from) !== null ? null : current;
}

/** Чого бракує у статусі ПДВ (порожньо — усе заповнено). */
export function vatPayerProblem(f: Pick<OrganizationCreate, 'is_vat_payer' | 'vat_number'>): string | null {
  if (f.is_vat_payer === null || f.is_vat_payer === undefined) return 'платник ПДВ (так / ні)';
  if (f.is_vat_payer && !(f.vat_number ?? '').trim()) return 'ІПН платника ПДВ';
  return null;
}

export default function VatPayerSelect(props: {
  value: boolean | null;
  fixed: boolean | null;
  onChange: (v: boolean) => void;
  invalid?: boolean;
  /** ч.1384: у збереженої організації статус змінюють лише в таблиці «Податковий статус за датами». */
  locked?: string;
}) {
  const { value, fixed, onChange, invalid, locked } = props;
  const v = value === null || value === undefined ? '' : value ? 'yes' : 'no';
  return (
    <>
      <label htmlFor="org-vat-payer">Платник ПДВ *</label>
      <select id="org-vat-payer" value={v} disabled={fixed !== null || !!locked}
        aria-invalid={invalid || undefined}
        title={locked || (fixed !== null ? 'Визначається системою оподаткування' : undefined)}
        style={invalid ? { borderColor: 'var(--badge-red-bd)' } : undefined}
        onChange={(e) => onChange(e.target.value === 'yes')}>
        {[...(v === '' ? [['', '— оберіть —']] : []), ['yes', 'так'], ['no', 'ні']].map(([k, label]) => (
          <option key={k} value={k}>{label}</option>
        ))}
      </select>
    </>
  );
}
